'use client';

import LoadingButton from '@mui/lab/LoadingButton';
import {
  AppBar,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Drawer,
  Slide,
  Toolbar,
} from '@mui/material';
import Stack from '@mui/material/Stack';
import { TransitionProps } from '@mui/material/transitions';
import Typography from '@mui/material/Typography';
import { utcToZonedTime } from 'date-fns-tz';
import { sortBy } from 'lodash';
import { useParams, useRouter } from 'next/navigation';
import { useQueryState } from 'nuqs';
import posthog from 'posthog-js';
import { forwardRef, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useGetEvent } from 'src/api/events';
import { useCreateWaitingListCart } from 'src/api/hooks/cart';
import { useGetWaitingListTickets } from 'src/api/hooks/waiting-list';
import { useAuthContext } from 'src/auth/hooks';
import EventPriceCard from 'src/components/event-price-card/event-price-card';
import FormProvider from 'src/components/hook-form';
import { useSnackbar } from 'src/components/snackbar';
import { useResponsive } from 'src/hooks/use-responsive';
import { useTranslate } from 'src/locales';
import { CartItemDTO, TicketItemDTO } from 'src/types';

import LastMinuteOptIn from './last-minute';
import TicketAvailable from './ticket-available';
import { useTicketCalculations } from './useTicketCalculations';
import PaymentHoldAlert from './wait-list-process-box';

const Transition = forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement;
    },
    ref: React.Ref<unknown>
  ) => <Slide direction="up" ref={ref} {...props} />
);

const FLAT_FEE = 30;

export function isLessThanFiveHoursUntilEvent(
  eventStart: string | Date,
  timeZone: string = 'America/Toronto'
): boolean {
  const now = new Date(); // local time
  const eventStartUtc = typeof eventStart === 'string' ? new Date(eventStart) : eventStart;

  const eventZoned = utcToZonedTime(eventStartUtc, timeZone);
  const nowZoned = utcToZonedTime(now, timeZone);

  const diffInMs = eventZoned.getTime() - nowZoned.getTime();
  const diffInHours = diffInMs / (1000 * 60 * 60);

  return Math.abs(diffInHours) < 5; // true if within ±5 hours of start
}

const convertTicketItem = (ticketItems: CartItemDTO[]): TicketItemDTO[] =>
  ticketItems
    .filter((item) => item.quantityInCart > 0) // Filter items that have quantityInCart > 0
    .map((item) => ({
      ticketSettingId: item.id,
      quantity: item.quantityInCart,
    }));

export default function EventWaitingListCartModal() {
  const mdUp = useResponsive('up', 'md');
  const { t } = useTranslate('common');
  const { t: tEvent } = useTranslate('event');
  const { enqueueSnackbar } = useSnackbar();
  const { authenticated, user } = useAuthContext();
  const router = useRouter();

  const { id } = useParams<{ id: string }>();
  const { data: event } = useGetEvent(id, user);

  const [waitingList, setWaitingList] = useQueryState('waiting-list');
  const { data: ticketList } = useGetWaitingListTickets(Number(id), true);
  const [__, setOpenLogin] = useQueryState('login');

  const { mutateAsync: joinWaitingList } = useCreateWaitingListCart(Number(id));

  const isLessThanFiveHours = event?.startDate
    ? isLessThanFiveHoursUntilEvent(event?.startDate)
    : false;

  console.log('isLessThanFiveHours', isLessThanFiveHours);

  const defaultValues = useMemo(
    () => ({
      tickets: sortBy(ticketList, 'price')?.map((ticket) => ({
        ...ticket,
        quantityInCart: 0,
      })),
      lastMinuteOptIn: !!isLessThanFiveHours,
    }),
    [ticketList, isLessThanFiveHours]
  );

  const methods = useForm({
    defaultValues,
  });
  const {
    watch,
    control,
    getValues,
    reset,
    handleSubmit,
    formState: { isSubmitting, dirtyFields },
  } = methods;

  const tickets = watch('tickets') as CartItemDTO[];
  const lastMinuteOptIn = watch('lastMinuteOptIn') || false;

  const handleClose = () => {
    setWaitingList(null);
  };

  const {
    serviceFees,
    promoCode,
    discountedTotalCost,
    isFreeTicket,
    appliedDiscount,
    hasTicketWithQuantityMoreThanZero,
    hasShopItemWithQuantityMoreThanZero,
    handleIncrease,
    handleDecrease,
  } = useTicketCalculations({
    tickets,
    shopItemsList: [],
    getValues,
    flatFee: FLAT_FEE,
    eventId: Number(id),
  });

  useEffect(() => {
    if (ticketList) {
      reset(defaultValues);
    }
  }, [defaultValues, ticketList, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (!authenticated) {
        setOpenLogin('open');
        return;
      }

      const cart = await joinWaitingList({
        items: convertTicketItem(data.tickets),
        lastMinuteOptIn: isLessThanFiveHours ? true : data?.lastMinuteOptIn,
      });

      const params = new URLSearchParams();
      params.append('eventId', id);
      const cartUrl = `/cart/${cart.id}?${params.toString()}`;
      console.log('Navigating to:', cartUrl, cart);

      // Use both router and window.location to ensure navigation happens
      router.push(cartUrl);
      setTimeout(() => {
        window.location.href = cartUrl;
      }, 500); // Fallback after 500ms

      posthog.capture('view_cart', cart);

      handleClose();
      reset();
    } catch (error) {
      enqueueSnackbar(error, { variant: 'error' });
    }
  });

  const isDirty = Object.keys(dirtyFields).includes('tickets');

  console.log('isDirty', isDirty, Object.keys(dirtyFields));

  const subTotal = (
    <Stack spacing={1} sx={{ px: 2 }}>
      <Typography variant="h4">{tEvent('cart.breakdown')}</Typography>
      <EventPriceCard
        appliedPromoCode={promoCode}
        fees={serviceFees}
        isFreeTicket={isFreeTicket}
        currency={event?.currency}
        total={discountedTotalCost}
        discount={appliedDiscount}
      />
      <Divider sx={{ width: '100%' }} />
    </Stack>
  );

  return mdUp ? (
    <Drawer
      anchor="right"
      open={Boolean(waitingList)}
      onClose={() => handleClose()}
      sx={{
        '& .MuiDrawer-paper': {
          boxShadow: 'none',
          width: '40vw',
          height: '100%',
          p: 3,
          '&::-webkit-scrollbar': {
            display: 'none', // Hide the scrollbar
          },
        },
      }}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar sx={{ justifyContent: 'space-between', px: 2 }}>
          <Typography variant="h6">{tEvent('waiting-list.title')}</Typography>
          <Button variant="outlined" onClick={handleClose}>
            {t('buttons.close')}
          </Button>
        </Toolbar>
      </AppBar>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Stack
          maxHeight="100vh"
          sx={{
            overflow: 'scroll',
            '&::-webkit-scrollbar': {
              display: 'none', // Hide the scrollbar
            },
          }}
        >
          <Stack gap={2} px={2} pt={2}>
            <PaymentHoldAlert />
            {tickets?.map((ticket, index) => (
              <TicketAvailable
                key={index}
                ticket={ticket}
                index={index}
                control={control}
                handleIncrease={handleIncrease}
                handleDecrease={handleDecrease}
              />
            ))}
            {!isLessThanFiveHours && <LastMinuteOptIn />}
          </Stack>
          <Stack
            spacing={2.5}
            component="div"
            sx={{
              p: 2,
              pb: 4,
              position: 'absolute',
              left: 0,
              borderTopLeftRadius: 3,
              borderTopRightRadius: 3,
              bottom: 0,
              backgroundColor: '#000',
              width: '100%',
            }}
          >
            {(hasTicketWithQuantityMoreThanZero || hasShopItemWithQuantityMoreThanZero) && subTotal}
            <LoadingButton
              fullWidth
              color="inherit"
              size="large"
              disabled={!isDirty}
              loading={isSubmitting}
              type="submit"
              variant="contained"
            >
              {tEvent('waiting-list.button')}
            </LoadingButton>
            <Typography
              variant="caption"
              sx={{ color: 'grey.500', display: 'block', textAlign: 'center', mt: 1 }}
            >
              {lastMinuteOptIn
                ? "You'll be considered for acceptance until the event begins"
                : "You'll only be considered for acceptance up to 5 hours before the event"}
            </Typography>
          </Stack>
        </Stack>
      </FormProvider>
    </Drawer>
  ) : (
    <Dialog
      fullScreen
      open={Boolean(waitingList)}
      onClose={() => handleClose()}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative', bgcolor: '#000' }}>
        <Toolbar sx={{ justifyContent: 'space-between', px: 2 }}>
          <Typography variant="h6">{tEvent('waiting-list.title')}</Typography>
          <Button variant="outlined" onClick={handleClose}>
            {t('buttons.close')}
          </Button>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{ p: 0, pb: 15 }}>
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <Stack gap={2} px={2} pt={2} pb={35}>
            <PaymentHoldAlert />
            {tickets?.map((ticket, index) => (
              <TicketAvailable
                key={index}
                ticket={ticket}
                index={index}
                control={control}
                currency={event?.currency}
                handleIncrease={handleIncrease}
                handleDecrease={handleDecrease}
              />
            ))}
            {!isLessThanFiveHours && <LastMinuteOptIn />}
          </Stack>

          <Stack
            spacing={2.5}
            component="div"
            sx={{
              p: 2,
              pb: 4,
              position: 'absolute',
              left: 0,
              bottom: 0,
              borderTopLeftRadius: 15,
              borderTopRightRadius: 15,
              backgroundColor: '#000',
              width: '100%',
            }}
          >
            {(hasTicketWithQuantityMoreThanZero || hasShopItemWithQuantityMoreThanZero) && subTotal}
            <LoadingButton
              fullWidth
              color="inherit"
              size="large"
              disabled={!isDirty}
              loading={isSubmitting}
              type="submit"
              variant="contained"
            >
              {tEvent('waiting-list.button')}
            </LoadingButton>
            <Typography
              variant="caption"
              sx={{ color: 'grey.500', display: 'block', textAlign: 'center', mt: 1 }}
            >
              {lastMinuteOptIn
                ? "You'll be considered for acceptance until the event begins"
                : "You'll only be considered for acceptance up to 5 hours before the event"}
            </Typography>
          </Stack>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
